import { useEffect, useState } from 'react'
import { MdSearch, MdShield } from 'react-icons/md'
import { getRoles } from '@/lib/api/roles/roles-api'
import type { Role } from '@/lib/api/common/interfaces/role.interface'

interface Props {
    selected: number[]
    onChange: (roleIds: number[]) => void
    disabled?: boolean
    /** Aviso quando não há perfis cadastrados. Padrão: o texto das pastas. */
    emptyMessage?: string
    /** Texto de ajuda abaixo da lista. Padrão: o texto das pastas. */
    helpText?: string
    /**
     * Perfis que já têm acesso por outra regra (no funil: os que veem todos os funis).
     * Aparecem marcados e travados, com `lockedLabel`, e não entram no `onChange`.
     */
    lockedIds?: number[]
    lockedLabel?: string
}

/**
 * Seleção dos perfis de acesso liberados numa pasta restrita.
 *
 * Usado tanto na criação quanto na edição, para que as duas telas mostrem
 * exatamente a mesma lista e o mesmo comportamento.
 */
export default function StorageRolePicker({
    selected,
    onChange,
    disabled,
    emptyMessage,
    helpText,
    lockedIds = [],
    lockedLabel,
}: Props) {
    const [roles, setRoles] = useState<Role[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')

    useEffect(() => {
        let active = true

        void (async () => {
            const response = await getRoles()
            if (!active) return
            setRoles(response.success ? (response.result ?? []) : [])
            setLoading(false)
        })()

        return () => {
            active = false
        }
    }, [])

    const toggle = (roleId: number) => {
        onChange(
            selected.includes(roleId)
                ? selected.filter(id => id !== roleId)
                : [...selected, roleId],
        )
    }

    const filtered = search
        ? roles.filter(role =>
              role.name.toLowerCase().includes(search.trim().toLowerCase()),
          )
        : roles

    if (loading) {
        return (
            <div className="flex justify-center py-6">
                <span className="loading loading-spinner loading-md text-primary" />
            </div>
        )
    }

    if (!roles.length) {
        return (
            <div className="alert alert-warning py-2">
                <span className="text-sm">
                    {emptyMessage ??
                        'Nenhum perfil de acesso cadastrado. Crie perfis em Segurança > Permissões antes de restringir a pasta.'}
                </span>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {roles.length > 6 && (
                <div className="join w-full">
                    <span className="btn btn-sm join-item btn-ghost border border-base-300 pointer-events-none">
                        <MdSearch />
                    </span>
                    <input
                        type="text"
                        className="input input-sm input-bordered join-item w-full"
                        placeholder="Buscar perfil"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                    />
                </div>
            )}

            <div className="max-h-56 overflow-y-auto border border-base-300 rounded-lg divide-y divide-base-200">
                {filtered.map(role => {
                    const locked = lockedIds.includes(role.id)
                    return (
                        <label
                            key={role.id}
                            className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
                                locked ? 'cursor-default bg-base-200/40' : 'cursor-pointer hover:bg-base-200'
                            } ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
                            title={locked ? lockedLabel : undefined}
                        >
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm"
                                checked={locked || selected.includes(role.id)}
                                disabled={disabled || locked}
                                onChange={() => toggle(role.id)}
                            />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{role.name}</p>
                                {role.description && (
                                    <p className="text-xs text-base-content/60 truncate">
                                        {role.description}
                                    </p>
                                )}
                            </div>
                            {locked && lockedLabel && (
                                <span className="badge badge-ghost badge-sm shrink-0 whitespace-nowrap">{lockedLabel}</span>
                            )}
                        </label>
                    )
                })}

                {!filtered.length && (
                    <p className="text-sm text-base-content/60 px-3 py-4 text-center">
                        Nenhum perfil encontrado
                    </p>
                )}
            </div>

            <p className="text-xs text-base-content/60 flex items-start gap-1.5">
                <MdShield className="text-base shrink-0 mt-0.5" />
                {helpText ??
                    'Somente os perfis marcados verão esta pasta e o conteúdo dela, incluindo as subpastas. Quem criou a pasta mantém o acesso.'}
            </p>
        </div>
    )
}
